/* eslint-disable @typescript-eslint/no-explicit-any */

import { type JSX, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect, Route, type RouteComponentProps, Switch, withRouter } from 'react-router-dom';

import { Box, Button } from '@mui/material';
import { useOktaAuth } from '@okta/okta-react';

import { Header, SplashLoader } from '../../components';
import CustomerRequestCard from '../../components/cards/dashboard/RequestList';
import { environment } from '../../environment';
import { logger } from '../../features/logger';
import { withAPIService } from '../../networking';
import { errorDialogNotFound, errorDialogRedirect } from '../../shared/dialogConfigs';
import { languageSelection } from '../../shared/translationConfig/helper';
import { withStore } from '../../store';
import { openDialog } from '../../store/dialog';
import { multipleRequests } from '../../store/multiRequest';
import { singleCustomer } from '../../store/singleRecord';
import { logoutAndRedirectToLogin } from '../../utils/auth/logout';
import { PAGE_ROUTES } from '../../utils/constants';
import { filterValidRequests } from '../../utils/requestFilterHelper';
import { singleRecordCustomer } from '../../utils/routeFormatter';
import { updateLocalStorage, updateSessionStorage } from '../../utils/storage';
import timer from '../../utils/timer';
import NotFoundPage from '../NotFoundPage';
import OnboardingForm from '../OnboardingForm';

function getTopPadding(pathname: string): number {
  if (pathname.split('/').reverse()[0] === PAGE_ROUTES.review) {
    return 250;
  }
  return 0;
}

function handleError(e: any, history: RouteComponentProps['history'], dispatch: any) {
  const errorDialog = errorDialogRedirect(history);

  if (e.message.includes('404')) {
    dispatch(openDialog(errorDialogNotFound(history)));
  } else {
    dispatch(openDialog(errorDialog));
  }
}

interface DashboardProps extends RouteComponentProps {
  dispatch: any;
  apiService: any;
  store: any;
}

export function DashboardComponent({ location, history, dispatch, apiService, store }: DashboardProps): JSX.Element {
  const { singleRecord, singleRecord: { status } = { status: true } } = store;

  const { pathname } = location;

  const { t, i18n } = useTranslation();
  const { oktaAuth } = useOktaAuth();

  const [requests, setRequestData] = useState<any[]>([]);

  const [contactName, setContactName] = useState<string>('');

  const [hasLoaded, setHasLoaded] = useState<boolean>(false);

  const message = `${t('dashboard.greeting')}, ${contactName?.charAt(0).toUpperCase() + contactName.slice(1)}`;

  useEffect(() => {
    const sessionLang = languageSelection();

    i18n.changeLanguage(sessionLang || (requests as any)?.languageCode || 'en');
  }, [history.push, location.pathname, i18n, (requests as any)?.languageCode]);

  useEffect(() => {
    if (!requests.length) {
      apiService
        .getActiveRequests(dispatch)
        .then((response: any) => {
          logger.info('RED-CARPET-FE Dashboard Component getActiveRequests API response successfully.');

          const { requests: reqs, contactFirstName } = response;

          const filteredRequests = filterValidRequests(reqs);

          setRequestData(filteredRequests);
          setContactName(contactFirstName);

          dispatch(multipleRequests(filteredRequests));
        })
        .catch((e: any) => {
          logger.error('Red-Carpet-FE-getActiveRequests-API FAILED', { e });

          handleError(e, history, dispatch);
        })
        .finally(() => {
          setHasLoaded(true);
        });
    }
  }, [apiService, dispatch]);

  useEffect(() => {
    const cleanup = timer(
      () =>
        apiService.revokeToken().then(() => {
          logger.info('Dashboard idle logout triggered');

          logoutAndRedirectToLogin(oktaAuth);
        }),
      30,
      'min'
    );

    return cleanup;
  }, [apiService, oktaAuth]);

  useEffect(() => {
    if (requests.length === 1 && singleRecord && status === true) {
      updateSessionStorage.setItem('hcId', requests[0].id);

      dispatch(singleCustomer(false));

      singleRecordCustomer(requests, location, history);
    }
  }, [requests, singleRecord, status, location, history, dispatch]);

  const redirectRoute = pathname || '/secure';

  const routes = (
    <Switch>
      <Route exact path="/">
        <Redirect to={redirectRoute} />
      </Route>
      <Route exact path="/secure">
        <Redirect to="/secure/dashboard" />
      </Route>
      <Route
        exact
        path="/secure/dashboard"
        render={() => <CustomerRequestCard requests={requests} contactName={contactName} />}
      />
      <Route path="/secure/form" render={() => <OnboardingForm firstName={contactName} />} />
      <Route path="/secure/:rest" component={NotFoundPage} />
    </Switch>
  );

  const splittedPath = pathname.split('/');
  const pagePath = splittedPath[splittedPath.length - 1];

  const isDashboard = pagePath === PAGE_ROUTES.dashboard;

  const toppadding = isDashboard ? getTopPadding(pathname) : 0;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        pt: `${toppadding}px`,
        width: '100%',
        minHeight: '98vh'
      }}
    >
      {isDashboard && <Header title={message} pathname={pathname} formPages={[{ route: 'dashboard' }]} />}
      {requests.length ? (
        routes
      ) : import.meta.env.DEV && hasLoaded ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            mt: 8
          }}
        >
          <span>No active requests found.</span>
          <Button
            variant="contained"
            onClick={async () => {
              // End the Okta org (SSO) session first. Clearing only the local
              // tokens leaves the Okta session active, so the login page
              // silently re-authenticates via getWithoutPrompt and bounces
              // back to /secure. closeSession deletes that server session.
              try {
                await oktaAuth.closeSession();
              } catch {
                // no active session / CORS - continue clearing locally
              }
              oktaAuth.tokenManager.clear();
              oktaAuth.clearStorage();
              updateSessionStorage.clear();
              updateLocalStorage.clear();
              window.location.href = `${environment().server.path}/login`;
            }}
          >
            Log out
          </Button>
        </Box>
      ) : (
        <SplashLoader />
      )}
    </Box>
  );
}

const connectRouter = withRouter(DashboardComponent);

const connectAPI = withAPIService(connectRouter);

const Dashboard = withStore(connectAPI);

export default Dashboard;
